import React from 'react';
import { cn } from '../../utils/cn';
import { Check } from 'lucide-react';

const Checkbox = React.forwardRef(({
  className,
  checked = false,
  onChange,
  disabled = false,
  required = false,
  label,
  error,
  ...props
}, ref) => {
  const baseClasses = 'peer h-4 w-4 shrink-0 rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
  
  const checkedClasses = checked ? 'bg-primary-500 border-primary-500 text-white' : '';
  const errorClasses = error ? 'border-red-500 focus:ring-red-500' : '';
  
  const classes = cn(
    baseClasses,
    checkedClasses,
    errorClasses,
    className
  );

  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.checked);
    }
  };

  return (
    <div className="flex items-center space-x-2">
      <div className="relative">
        <input
          type="checkbox"
          className="sr-only"
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          required={required}
          {...props}
        />
        <div
          ref={ref}
          className={classes}
          onClick={() => !disabled && onChange && onChange(!checked)}
          role="checkbox"
          aria-checked={checked}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              if (!disabled && onChange) {
                onChange(!checked);
              }
            }
          }}
        >
          {checked && (
            <Check className="h-3 w-3 text-white" />
          )}
        </div>
      </div>
      {label && (
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
          {label}
        </label>
      )}
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
});

Checkbox.displayName = 'Checkbox';

const Radio = React.forwardRef(({
  className,
  checked = false,
  onChange,
  disabled = false,
  required = false,
  label,
  value,
  name,
  error,
  ...props
}, ref) => {
  const baseClasses = 'peer h-4 w-4 shrink-0 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
  
  const checkedClasses = checked ? 'bg-primary-500 border-primary-500' : '';
  const errorClasses = error ? 'border-red-500 focus:ring-red-500' : '';
  
  const classes = cn(
    baseClasses,
    checkedClasses,
    errorClasses,
    className
  );

  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  return (
    <div className="flex items-center space-x-2">
      <div className="relative">
        <input
          type="radio"
          className="sr-only"
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          required={required}
          value={value}
          name={name}
          {...props}
        />
        <div
          ref={ref}
          className={classes}
          onClick={() => !disabled && onChange && onChange(value)}
          role="radio"
          aria-checked={checked}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              if (!disabled && onChange) {
                onChange(value);
              }
            }
          }}
        >
          {checked && (
            <div className="h-2 w-2 rounded-full bg-white mx-auto mt-0.5" />
          )}
        </div>
      </div>
      {label && (
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
          {label}
        </label>
      )}
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
});

Radio.displayName = 'Radio';

export { Checkbox, Radio };
